import React, { useEffect, useState } from "react";
import {
  Breadcrumb,
  Row,
  Col,
  Card,
  Space,
  Button,
  Image,
  Modal,
  notification,
} from "antd";
import { Table } from "ant-table-extensions";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { useMediaQuery } from "react-responsive";

import Create from "./Create";
import Edit from "./Edit";

import axios from "axios"; // <-- ADDED
import config from "../../../../config/config";
import urldata from "../../../../urldata";

const { confirm } = Modal;

function Index() {
  const isMobile = useMediaQuery({ query: "(max-width: 576px)" });

  const [btnSize, setBtnSize] = useState("middle");
  const [datas, setDatas] = useState([]);
  const [loading, setLoading] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [createKey, setCreateKey] = useState(1);

  const [editOpen, setEditOpen] = useState(false);
  const [editKey, setEditKey] = useState(1);
  const [selectedRecord, setSelectedRecord] = useState(null);

  const moduleName = "Career Value";
  const moduleNamePlural = "We Believe";

  const base_api = urldata + "admin/";
  const getEndpoint = base_api + "getCareerValues";
  const azure_base = config.azure_object_url || "";

  // ---------------------------
  // GET DATA USING AXIOS
  // ---------------------------
  const fetchDatas = async () => {
    setLoading(true);
    try {
      const response = await axios.get(getEndpoint);

      const list =
        response?.data?.data && Array.isArray(response.data.data)
          ? response.data.data
          : [];

      setDatas(list);

      if (list.length === 0) {
        notification.info({ message: "No career values found" });
      }
    } catch (err) {
      console.error(err);
      notification.error({ message: "Failed to fetch data" });
      setDatas([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDatas();
  }, []);

  useEffect(() => {
    setBtnSize(isMobile ? "small" : "middle");
  }, [isMobile]);

  // ---------------------------
  // CREATE HANDLERS
  // ---------------------------
  const handleCreateOpen = () => setCreateOpen(true);

  const handleCreateClose = () => {
    setCreateKey((k) => k + 1);
    setCreateOpen(false);
    fetchDatas();
  };

  // ---------------------------
  // EDIT HANDLERS
  // ---------------------------
  const handleEditOpen = (record) => {
    const transformedRecord = {
      ...record,
      bgImg: record.bgImg
        ? [
            {
              uid: "-1",
              name: "bgImg.png",
              status: "done",
              url: azure_base + record.bgImg,
            },
          ]
        : [],
      mbBgImg: record.mbBgImg
        ? [
            {
              uid: "-2",
              name: "mbBgImg.png",
              status: "done",
              url: azure_base + record.mbBgImg,
            },
          ]
        : [],
      icon: record.icon
        ? [
            {
              uid: "-3",
              name: "icon.png",
              status: "done",
              url: azure_base + record.icon,
            },
          ]
        : [],
    };

    setSelectedRecord(transformedRecord);
    setEditOpen(true);
  };

  const handleEditClose = () => {
    setEditKey((k) => k + 1);
    setSelectedRecord(null);
    setEditOpen(false);
    fetchDatas();
  };

  // ---------------------------
  // DELETE USING AXIOS
  // ---------------------------
  const handleDelete = (id) => {
    confirm({
      title: `Are you sure you want to delete this ${moduleName}?`,
      icon: <ExclamationCircleOutlined />,
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      async onOk() {
        try {
          const deleteEndpoint = base_api + "deleteCareerValue";

          const result = await axios.post(deleteEndpoint, { id });

          if (result) {
            notification.success({
              message: `${moduleName} deleted successfully`,
            });
            fetchDatas();
          }
        } catch (err) {
          console.error(err);
          notification.error({ message: "Failed to delete" });
        }
      },
    });
  };

  // ---------------------------
  // TABLE COLUMNS
  // ---------------------------
  const columns = [
    { title: "Title", dataIndex: "title", key: "title" },
    { title: "Description", dataIndex: "desc", key: "desc" },
    { title: "Initial", dataIndex: "initial", key: "initial" },

    {
      title: "Web Image",
      key: "bgImg",
      render: (_, record) => (
        <Image
          width={isMobile ? 80 : 100}
          src={record.bgImg ? `${azure_base}${record.bgImg}` : undefined}
          fallback="https://via.placeholder.com/100"
          preview
        />
      ),
    },

    {
      title: "Mobile Image",
      key: "mbBgImg",
      render: (_, record) => (
        <Image
          width={isMobile ? 80 : 100}
          src={record.mbBgImg ? `${azure_base}${record.mbBgImg}` : undefined}
          fallback="https://via.placeholder.com/100"
          preview
        />
      ),
    },

    {
      title: "Icon",
      key: "icon",
      render: (_, record) => (
        <Image
          width={60}
          src={record.icon ? `${azure_base}${record.icon}` : undefined}
          fallback="https://via.placeholder.com/80"
          preview
        />
      ),
    },

    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="primary"
            size={btnSize}
            onClick={() => handleEditOpen(record)}
          >
            Edit
          </Button>

          <Button
            type="danger"
            size={btnSize}
            onClick={() => handleDelete(record._id)}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Breadcrumb style={{ margin: "16px 0" }}>
        <Breadcrumb.Item>Dashboard</Breadcrumb.Item>
        <Breadcrumb.Item>{moduleNamePlural}</Breadcrumb.Item>
      </Breadcrumb>

      <Row>
        <Col span={24}>
          <Card title={moduleNamePlural} bordered={false}>
            <Row style={{ marginBottom: 20 }}>
              <Col span={24}>
                <Space>
                  <Button
                    type="primary"
                    size={btnSize}
                    onClick={handleCreateOpen}
                  >
                    Add {moduleName}
                  </Button>
                </Space>
              </Col>
            </Row>

            <Table
              loading={loading}
              rowKey={(record) => record._id}
              columns={columns}
              dataSource={datas}
              footer={() => `Total ${moduleNamePlural} = ${datas.length}`}
            />

            {/* CREATE MODAL */}
            <Create
              key={"create" + createKey}
              isVisible={createOpen}
              handleClose={handleCreateClose}
              moduleName={moduleName}
              base_url={base_api}
            />

            {/* EDIT MODAL */}
            {selectedRecord && (
              <Edit
                key={"edit" + editKey}
                isVisible={editOpen}
                handleClose={handleEditClose}
                moduleName={moduleName}
                base_url={base_api}
                record={selectedRecord}
              />
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default Index;
